import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField } from "@/lib/crm";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { Empty, options } from "@/lib/assets";
import { ORDER_BILLING, ORDER_STATUS, OrderBilling, OrderStatus } from "@/lib/orders";
import { DemoBadge, Panel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreateOrderDialog } from "@/components/orders/CreateOrderDialog";

export const OrdersList = ({ company }) => {
  const { can } = useAuth();
  const [flt, setFlt] = useState({ q: "", status: "", billing_status: "" });
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    const p = Object.fromEntries(Object.entries({ ...flt, company_id: company?.id }).filter(([, v]) => v));
    api.get("/orders", { params: p }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [flt, company?.id]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  return (
    <Panel className="p-4 md:p-5" data-testid="orders-list">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="relative min-w-[200px] flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Cerca numero ordine, ordine cliente, offerta" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} data-testid="orders-search" /></div>
        <SelectField value={flt.status} onChange={(v) => setFlt({ ...flt, status: v })} options={options(ORDER_STATUS)} allowNone="Tutti gli stati" placeholder="Stato" testId="orders-filter-status" className="w-48" />
        <SelectField value={flt.billing_status} onChange={(v) => setFlt({ ...flt, billing_status: v })} options={options(ORDER_BILLING)} allowNone="Tutte le fatturazioni" placeholder="Fatturazione" testId="orders-filter-billing" className="w-48" />
        {can("ordini:create") && can("quotes:read") && <Button className="gap-1" onClick={() => setOpen(true)} data-testid="order-new"><Plus className="h-4 w-4" />Crea da offerta accettata</Button>}
      </div>
      {items && !items.length && <Empty testId="orders-empty">Nessun ordine registrato.</Empty>}
      <ul className="divide-y divide-line">{(items || []).map((o) => (
        <li key={o.id}><Link to={`/ordini/${o.id}`} className="flex flex-wrap items-center gap-3 py-3 hover:bg-slate-50/60" data-testid={`order-row-${o.id}`}>
          <div className="min-w-0 flex-1"><p className="font-mono text-xs font-semibold text-slate-400">{o.number}{o.customer_order_number ? ` · cliente n. ${o.customer_order_number}` : ""}</p>
            <p className="truncate text-sm font-semibold text-ink">{!company && `${o.company_name} · `}Offerta {o.quote_number} rev.{o.quote_revision} {o.is_demo && <DemoBadge className="ml-1" />}</p>
            <p className="text-xs text-slate-500">{fmtDate(o.order_date)} · {o.line_count} righe · {o.open_lines} da completare · {o.owner_name || "—"}</p></div>
          <span className="text-sm font-semibold text-ink" data-testid={`order-total-${o.id}`}>{fmtCurrency(o.taxable)}<span className="ml-1 text-[11px] font-normal text-slate-400">+ IVA</span></span>
          <OrderBilling o={o} testId={`order-billing-status-${o.id}`} />
          <OrderStatus value={o.status} testId={`order-status-${o.id}`} />
        </Link></li>))}</ul>
      <CreateOrderDialog open={open} onOpenChange={setOpen} companyId={company?.id} />
    </Panel>
  );
};
